import * as React from "react";
import { cn } from "@/lib/utils";
import Icon from "@/components/icons";
import { type IconTypes } from "@/components/icons/types";

type IconVariant = IconTypes | "none";

type BadgeColor = "primaryLight" | "primaryDark" | "puttyLight" | "accentPink";

const baseStyles =
  "inline-flex items-center gap-1 rounded-[0.5rem] px-2 py-1 text-xs font-bold";

const colorStyles: Record<BadgeColor, { default: string; withBorder: string }> =
  {
    primaryLight: {
      default: "bg-primary-light text-primary-foreground",
      withBorder:
        "border-2 border-primary-light bg-[#BEF4E5] text-primary-light",
    },
    primaryDark: {
      default: "bg-primary text-primary-foreground",
      withBorder: "border-2 border-primary bg-[#BEF4E5] text-primary-light",
    },
    puttyLight: {
      default: "bg-putty-light text-gray-900",
      withBorder: "border-2 border-putty-700 bg-putty-light text-gray-900",
    },
    accentPink: {
      default: "bg-accent-pink text-black",
      withBorder: "border-2 border-accent-pink bg-putty-light text-gray-900",
    },
  };

const iconVariantText: Partial<Record<IconTypes, string>> = {
  ai: "AI",
  business: "Business",
  data: "Data",
  popular: "Popular",
  sale: "Sale",
  "start-date": "Start Date",
  trending: "Trending",
};

interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  color?: BadgeColor;
  icon: IconVariant;
  children?: React.ReactNode;
  withBorder?: boolean;
  iconSize?: number;
}

const getIcon = (icon: IconVariant, iconSize?: number) => {
  if (icon === "none") {
    return null;
  }
  const svgProps = iconSize ? { width: iconSize, height: iconSize } : {};
  return <Icon type={icon} svgProps={{ ...svgProps }} />;
};

function BadgeIcon({
  className,
  color,
  icon = "none",
  children,
  withBorder = false,
  iconSize,
  ...props
}: BadgeProps) {
  const IconComponent = React.useMemo(
    () => getIcon(icon, iconSize),
    [icon, iconSize],
  );

  return (
    <div
      className={cn(
        baseStyles,
        withBorder ? "border" : "",
        colorStyles[color ?? "primaryLight"][
          withBorder ? "withBorder" : "default"
        ],
        className,
      )}
      {...props}
    >
      {IconComponent}
      {icon === "none" || children ? children : iconVariantText[icon] || ""}
    </div>
  );
}

export { BadgeIcon, type BadgeProps, type IconVariant, type BadgeColor };
